import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  UserSquare2,
  FileHeart,
  FileText,
  MessagesSquare,
  Loader2,
  Lock,
  ImageIcon,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const GENERO_LABEL = {
  mujer: "Mujer",
  hombre: "Hombre",
  mujer_trans: "Mujer trans",
  hombre_trans: "Hombre trans",
  intersex: "Intersex",
  nb: "No binaria/o",
  otro: "Otro",
};

const HABIT_LABEL = {
  no: "No",
  ocasional: "Ocasional",
  frecuente: "Frecuente",
  diario: "Diario",
  ninguna: "Ninguna",
};

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function MedicoExpedientePaciente() {
  const { patient_id } = useParams();
  const navigate = useNavigate();
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/medico/pacientes-compartidos/${patient_id}/expediente`,
      );
      setExpediente(data);
    } catch (e) {
      const msg = formatApiError(e);
      const status = e?.response?.status;
      setError({ status, msg });
    } finally {
      setLoading(false);
    }
  }, [patient_id]);

  useEffect(() => {
    load();
  }, [load]);

  const openLab = (lab) => {
    if (!lab?.download_url) {
      toast.error("URL de descarga inválida.");
      return;
    }
    window.open(lab.download_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10">
        <div className="mb-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/medico")}
            data-testid="expediente-back-btn"
          >
            <ArrowLeft size={14} /> Volver al panel
          </Button>
        </div>

        {loading && (
          <div className="rounded-xl border border-border bg-white p-8 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Cargando expediente compartido…
          </div>
        )}

        {!loading && error && (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 p-6"
            data-testid="expediente-error"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
              <div>
                <div className="text-sm font-semibold text-amber-900">
                  {error.status === 403
                    ? "No tienes acceso activo a este expediente"
                    : error.status === 404
                    ? "Persona usuaria no encontrada"
                    : "No pudimos cargar el expediente"}
                </div>
                <div className="text-xs text-amber-800 mt-1">
                  {error.status === 403
                    ? "Es posible que la persona usuaria haya revocado el acceso. Contáctala si esperabas verla aquí."
                    : error.msg}
                </div>
                <Link to="/medico" className="inline-block mt-3">
                  <Button variant="outline" size="sm" data-testid="expediente-back-error">
                    Volver al panel médico
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && expediente && (
          <>
            <ExpedienteHero paciente={expediente.paciente} />
            <PrivacyNote />
            <HistoriaClinicaSection historia={expediente.historia} />
            <LaboratoriosSection labs={expediente.laboratorios} onView={openLab} />
            <ConsultasSection consultas={expediente.consultas} />
          </>
        )}
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

// -------------------- Sections --------------------

function ExpedienteHero({ paciente }) {
  return (
    <section
      className="rounded-xl border border-border bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="expediente-hero"
    >
      <div className="w-14 h-14 rounded-full bg-[#0F2942] text-white grid place-items-center shrink-0">
        <UserSquare2 size={24} />
      </div>
      <div className="flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
          Expediente compartido
        </div>
        <h1
          className="font-display text-2xl font-semibold text-[#0F2942] tracking-tight mt-0.5"
          data-testid="expediente-paciente-nombre"
        >
          {paciente?.nombre_completo || "Persona usuaria"}
        </h1>
        {paciente?.created_at && (
          <div className="text-xs text-muted-foreground mt-1">
            Miembro desde{" "}
            {new Date(paciente.created_at).toLocaleDateString("es-MX", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function PrivacyNote() {
  return (
    <div
      className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2"
      data-testid="expediente-privacy-note"
    >
      <Lock className="shrink-0 mt-0.5" size={14} />
      <div>
        Acceso <b>solo lectura</b>. Los archivos abiertos usan URL firmada válida por 5 minutos.
        La persona usuaria puede revocar este acceso en cualquier momento.
      </div>
    </div>
  );
}

function HistoriaClinicaSection({ historia }) {
  const payload = historia?.payload;
  return (
    <section
      className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
      data-testid="expediente-historia-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center text-[#0F2942] shrink-0">
            <FileHeart size={18} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
              Historia clínica
            </div>
            <h2 className="font-display text-lg font-semibold text-[#0F2942] mt-0.5">
              Datos de la persona usuaria
            </h2>
            {historia?.updated_at && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Actualizada:{" "}
                {new Date(historia.updated_at).toLocaleDateString("es-MX", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            )}
          </div>
        </div>
        {historia?.complete ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
            <CheckCircle2 size={11} /> Completa
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
            <AlertTriangle size={11} /> Incompleta
          </span>
        )}
      </div>

      {!payload && (
        <div
          className="mt-4 rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
          data-testid="expediente-historia-empty"
        >
          La persona usuaria aún no ha capturado su historia clínica.
        </div>
      )}

      {payload && (
        <div className="mt-4 grid gap-4">
          <Demograficos data={payload.demograficos} />
          <Grid2>
            <ListaItems
              title="Alergias · fármacos"
              ninguna={payload.alergias?.farmacos?.ninguna}
              items={payload.alergias?.farmacos?.items}
              testid="hist-alergias-farmacos"
            />
            <ListaItems
              title="Alergias · alimentos"
              ninguna={payload.alergias?.alimentos?.ninguna}
              items={payload.alergias?.alimentos?.items}
              testid="hist-alergias-alimentos"
            />
          </Grid2>
          <Enfermedades data={payload.enfermedades_cronicas} />
          <Medicacion data={payload.medicacion_actual} />
          <Condiciones data={payload.condiciones_especiales} />
          <Habitos data={payload.habitos} />
        </div>
      )}
    </section>
  );
}

function LaboratoriosSection({ labs, onView }) {
  return (
    <section
      className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
      data-testid="expediente-labs-section"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center text-[#0F2942] shrink-0">
          <FileText size={18} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
            Laboratorios
          </div>
          <h2 className="font-display text-lg font-semibold text-[#0F2942] mt-0.5">
            Archivos cargados por la persona usuaria
          </h2>
        </div>
      </div>

      {(!labs || labs.length === 0) && (
        <div
          className="mt-4 rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
          data-testid="expediente-labs-empty"
        >
          La persona usuaria no ha subido laboratorios.
        </div>
      )}

      {labs && labs.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="expediente-labs-list">
          {labs.map((lab) => (
            <li
              key={lab.id}
              className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2"
              data-testid={`expediente-lab-${lab.id}`}
            >
              <div className="w-8 h-8 rounded bg-muted grid place-items-center text-[#0F2942] shrink-0">
                {lab.mime === "application/pdf" ? (
                  <FileText size={14} />
                ) : (
                  <ImageIcon size={14} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {lab.nombre}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {lab.mime} · {humanSize(lab.size)} ·{" "}
                  {lab.uploaded_at &&
                    new Date(lab.uploaded_at).toLocaleDateString("es-MX")}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(lab)}
                data-testid={`expediente-lab-view-${lab.id}`}
              >
                Ver archivo
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ConsultasSection({ consultas }) {
  return (
    <section
      className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
      data-testid="expediente-consultas-section"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center text-[#0F2942] shrink-0">
          <MessagesSquare size={18} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
            Consultas con IA
          </div>
          <h2 className="font-display text-lg font-semibold text-[#0F2942] mt-0.5">
            Repositorio de recomendaciones clínicas
          </h2>
        </div>
      </div>

      {(!consultas || consultas.length === 0) && (
        <div
          className="mt-4 rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
          data-testid="expediente-consultas-empty"
        >
          La persona usuaria aún no tiene consultas registradas.
        </div>
      )}

      {consultas && consultas.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="expediente-consultas-list">
          {consultas.map((c) => (
            <li
              key={c.id}
              className="rounded-lg border border-border bg-muted/20 p-4"
              data-testid={`expediente-consulta-${c.id}`}
            >
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    c.estado === "completada"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : c.estado === "procesando"
                      ? "border-blue-200 bg-blue-50 text-blue-800"
                      : c.estado === "requiere_info"
                      ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                      : "border-red-200 bg-red-50 text-red-800"
                  }`}
                >
                  {c.estado === "requiere_info"
                    ? "necesita más información"
                    : c.estado.replace("_", " ")}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {new Date(c.created_at).toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                {c.gpc_codes && c.gpc_codes.length > 0 && (
                  <span className="font-mono">· {c.gpc_codes.join(", ")}</span>
                )}
              </div>

              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-widest text-[#0F2942]/70 font-semibold">
                  Síntomas reportados
                </div>
                <div
                  className="text-sm text-foreground mt-1 whitespace-pre-wrap"
                  data-testid={`consulta-sintomas-${c.id}`}
                >
                  {c.sintomas || <span className="text-muted-foreground">(sin datos)</span>}
                </div>
              </div>

              {c.respuesta && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold flex items-center gap-1">
                    <Sparkles size={11} /> Recomendación IA
                  </div>
                  <div
                    className="text-sm text-foreground mt-1 whitespace-pre-wrap leading-relaxed bg-white rounded-md border border-border p-3"
                    data-testid={`consulta-respuesta-${c.id}`}
                  >
                    {c.respuesta}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// -------------------- Historia sub-blocks --------------------

function Grid2({ children }) {
  return <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-right" data-testid={testid}>
        {value || <span className="text-muted-foreground">—</span>}
      </span>
    </div>
  );
}

function SubCard({ title, children, testid }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-[#0F2942]/70 font-semibold mb-1.5">
        {title}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Demograficos({ data }) {
  if (!data) return null;
  const g = data.genero;
  const gLabel = g === "otro" ? data.genero_otro_texto || "Otro" : GENERO_LABEL[g] || "—";
  return (
    <SubCard title="Demográficos" testid="hist-demograficos">
      <div className="grid sm:grid-cols-2 gap-x-4">
        <Row label="Edad" value={data.edad ? `${data.edad} años` : ""} testid="dem-edad" />
        <Row label="Género" value={gLabel} testid="dem-genero" />
        <Row label="Peso" value={data.peso ? `${data.peso} kg` : ""} testid="dem-peso" />
        <Row label="Altura" value={data.altura ? `${data.altura} cm` : ""} testid="dem-altura" />
      </div>
    </SubCard>
  );
}

function ListaItems({ title, ninguna, items, testid }) {
  const arr = items || [];
  return (
    <SubCard title={title} testid={testid}>
      {ninguna ? (
        <div className="text-sm text-muted-foreground">Ninguna reportada.</div>
      ) : arr.length === 0 ? (
        <div className="text-sm text-muted-foreground">(sin datos)</div>
      ) : (
        <ul className="list-disc list-inside space-y-0.5 text-sm">
          {arr.map((it, i) => (
            <li key={i}>{typeof it === "string" ? it : JSON.stringify(it)}</li>
          ))}
        </ul>
      )}
    </SubCard>
  );
}

function Enfermedades({ data }) {
  if (!data) return null;
  const items = data.seleccionadas || [];
  const otra = (data.otra_texto || "").trim();
  const list = [...items, ...(otra ? [otra] : [])];
  return (
    <SubCard title="Enfermedades crónicas" testid="hist-enfermedades">
      {data.ninguna ? (
        <div className="text-sm text-muted-foreground">Ninguna reportada.</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-muted-foreground">(sin datos)</div>
      ) : (
        <ul className="list-disc list-inside space-y-0.5 text-sm">
          {list.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      )}
    </SubCard>
  );
}

function Medicacion({ data }) {
  if (!data) return null;
  const arr = data.items || [];
  return (
    <SubCard title="Medicación actual" testid="hist-medicacion">
      {data.ninguna ? (
        <div className="text-sm text-muted-foreground">Ninguna reportada.</div>
      ) : arr.length === 0 ? (
        <div className="text-sm text-muted-foreground">(sin datos)</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {arr.map((m, i) => {
            const line =
              typeof m === "string"
                ? m
                : [m.nombre, m.dosis, m.frecuencia].filter(Boolean).join(" · ");
            return <li key={i}>• {line}</li>;
          })}
        </ul>
      )}
    </SubCard>
  );
}

function Condiciones({ data }) {
  if (!data) return null;
  const flags = [];
  if (data.embarazo) flags.push("Embarazo");
  if (data.lactancia) flags.push("Lactancia");
  if (data.cirugia_reciente) flags.push("Cirugía reciente");
  return (
    <SubCard title="Condiciones especiales" testid="hist-condiciones">
      {flags.length === 0 && !data.detalle ? (
        <div className="text-sm text-muted-foreground">Ninguna reportada.</div>
      ) : (
        <>
          {flags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1">
              {flags.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-900 text-[11px] px-2 py-0.5"
                >
                  {f}
                </span>
              ))}
            </div>
          )}
          {data.detalle && (
            <div className="text-sm mt-1 whitespace-pre-wrap">{data.detalle}</div>
          )}
        </>
      )}
    </SubCard>
  );
}

function Habitos({ data }) {
  if (!data) return null;
  const drogas = data.drogas || {};
  const drogasLine =
    drogas.frecuencia === "ninguna" || !drogas.frecuencia
      ? "Ninguna"
      : `${HABIT_LABEL[drogas.frecuencia] || drogas.frecuencia}${
          drogas.detalle ? ` · ${drogas.detalle}` : ""
        }`;
  return (
    <SubCard title="Hábitos" testid="hist-habitos">
      <div className="grid sm:grid-cols-2 gap-x-4">
        <Row
          label="Tabaquismo"
          value={HABIT_LABEL[data.tabaquismo] || data.tabaquismo || "—"}
          testid="hab-tabaquismo"
        />
        <Row
          label="Alcohol"
          value={HABIT_LABEL[data.alcohol] || data.alcohol || "—"}
          testid="hab-alcohol"
        />
        <Row label="Drogas" value={drogasLine} testid="hab-drogas" />
      </div>
    </SubCard>
  );
}
